/**
 * Security utilities for authentication and data validation
 * Provides common security functions used across the application
 */

// Password strength requirements
export const PASSWORD_REQUIREMENTS = {
  MIN_LENGTH: 8,
  REQUIRE_UPPERCASE: true,
  REQUIRE_LOWERCASE: true,
  REQUIRE_NUMBERS: true,
  REQUIRE_SPECIAL_CHARS: true,
};

// Input validation patterns
export const VALIDATION_PATTERNS = {
  EMAIL: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
  USERNAME: /^[a-zA-Z0-9_]{3,20}$/,
  PHONE: /^\+?[1-9]\d{1,14}$/,
  NAME: /^[a-zA-Z\s'-]{1,50}$/,
  TOKEN: /^[a-zA-Z0-9\-_\.]+$/, // For reset tokens, API keys, etc.
};

// Security constants
export const SECURITY_CONSTANTS = {
  MAX_LOGIN_ATTEMPTS: 5,
  ACCOUNT_LOCKOUT_DURATION: 15 * 60 * 1000, // 15 minutes
  TOKEN_EXPIRY_BUFFER: 5 * 60, // 5 minutes in seconds
  CSRF_TOKEN_LENGTH: 32,
};

// Valid authentication modes for URL parameter validation
export const VALID_MODES = new Set(['login', 'signup', 'reset']);

/**
 * Validates password strength against security requirements
 * @param {string} password - Password to validate
 * @returns {Object} Validation result with details
 */
export const validatePasswordStrength = (password) => {
  const result = {
    isValid: false,
    errors: [],
    score: 0,
  };

  if (!password) {
    result.errors.push('Password is required');
    return result;
  }

  // Length check
  if (password.length < PASSWORD_REQUIREMENTS.MIN_LENGTH) {
    result.errors.push(
      `Password must be at least ${PASSWORD_REQUIREMENTS.MIN_LENGTH} characters long`
    );
  } else {
    result.score += 1;
  }

  // Uppercase check
  if (PASSWORD_REQUIREMENTS.REQUIRE_UPPERCASE && !/[A-Z]/.test(password)) {
    result.errors.push('Password must contain at least one uppercase letter');
  } else if (PASSWORD_REQUIREMENTS.REQUIRE_UPPERCASE) {
    result.score += 1;
  }

  // Lowercase check
  if (PASSWORD_REQUIREMENTS.REQUIRE_LOWERCASE && !/[a-z]/.test(password)) {
    result.errors.push('Password must contain at least one lowercase letter');
  } else if (PASSWORD_REQUIREMENTS.REQUIRE_LOWERCASE) {
    result.score += 1;
  }

  // Numbers check
  if (PASSWORD_REQUIREMENTS.REQUIRE_NUMBERS && !/\d/.test(password)) {
    result.errors.push('Password must contain at least one number');
  } else if (PASSWORD_REQUIREMENTS.REQUIRE_NUMBERS) {
    result.score += 1;
  }

  // Special characters check
  if (
    PASSWORD_REQUIREMENTS.REQUIRE_SPECIAL_CHARS &&
    !/[!@#$%^&*(),.?":{}|<>]/.test(password)
  ) {
    result.errors.push('Password must contain at least one special character');
  } else if (PASSWORD_REQUIREMENTS.REQUIRE_SPECIAL_CHARS) {
    result.score += 1;
  }

  // Common password patterns to avoid
  const commonPatterns = [
    /123456/,
    /password/i,
    /qwerty/i,
    /admin/i,
    /letmein/i,
    /welcome/i,
    /monkey/i,
    /dragon/i,
    /master/i,
    /login/i,
  ];

  for (const pattern of commonPatterns) {
    if (pattern.test(password)) {
      result.errors.push(
        'Password contains common patterns that are not secure'
      );
      result.score -= 1;
      break;
    }
  }

  result.isValid = result.errors.length === 0 && result.score >= 4;
  return result;
};

/**
 * Validates JWT token format and expiration
 * @param {string} token - JWT token to validate
 * @returns {Object} Validation result
 */
export const validateJWTToken = (token) => {
  const result = {
    isValid: false,
    isExpired: false,
    expiresSoon: false,
    payload: null,
    error: null,
  };

  if (!token || typeof token !== 'string') {
    result.error = 'Token is required and must be a string';
    return result;
  }

  try {
    const parts = token.split('.');
    if (parts.length !== 3) {
      result.error = 'Invalid JWT format';
      return result;
    }

    // Decode payload
    const payload = JSON.parse(atob(parts[1]));
    result.payload = payload;

    const currentTime = Math.floor(Date.now() / 1000);

    // Check expiration
    if (!payload.exp) {
      result.error = 'Token missing expiration claim';
      return result;
    }

    result.isExpired = payload.exp <= currentTime;
    result.expiresSoon =
      payload.exp <= currentTime + SECURITY_CONSTANTS.TOKEN_EXPIRY_BUFFER;
    result.isValid = !result.isExpired;
  } catch (error) {
    result.error = `Token validation failed: ${error.message}`;
  }

  return result;
};

/**
 * Sanitizes and validates user input
 * @param {string} input - Input to sanitize
 * @param {string} type - Type of input (email, username, name, etc.)
 * @returns {Object} Sanitization result
 */
export const sanitizeInput = (input, type) => {
  const result = {
    isValid: false,
    sanitized: null,
    error: null,
  };

  if (!input || typeof input !== 'string') {
    result.error = 'Input is required and must be a string';
    return result;
  }

  // Basic sanitization
  const sanitized = input.trim().replace(/\s+/g, ' ');
  result.sanitized = sanitized;

  // Type-specific validation
  switch (type) {
    case 'email':
      if (!VALIDATION_PATTERNS.EMAIL.test(sanitized)) {
        result.error = 'Invalid email format';
        return result;
      }
      break;

    case 'username':
      if (!VALIDATION_PATTERNS.USERNAME.test(sanitized)) {
        result.error =
          'Username must be 3-20 characters and contain only letters, numbers, and underscores';
        return result;
      }
      break;

    case 'name':
      if (!VALIDATION_PATTERNS.NAME.test(sanitized)) {
        result.error =
          'Name can only contain letters, spaces, hyphens, and apostrophes';
        return result;
      }
      break;

    case 'phone':
      if (sanitized && !VALIDATION_PATTERNS.PHONE.test(sanitized)) {
        result.error =
          'Invalid phone number format (use E.164 format: +1234567890)';
        return result;
      }
      break;

    case 'token':
      if (!VALIDATION_PATTERNS.TOKEN.test(sanitized)) {
        result.error = 'Invalid token format';
        return result;
      }
      break;

    case 'password':
      // Password validation - basic sanitization, strength validation handled separately
      if (sanitized.length < PASSWORD_REQUIREMENTS.MIN_LENGTH) {
        result.error = `Password must be at least ${PASSWORD_REQUIREMENTS.MIN_LENGTH} characters long`;
        return result;
      }
      // Remove potential dangerous characters but allow most special characters for passwords
      const sanitizedPassword = sanitized.replace(/[\x00-\x1F\x7F]/g, '');
      result.sanitized = sanitizedPassword;
      break;

    default:
      // Generic validation - prevent XSS and injection attacks
      const dangerousPatterns = [
        /<script[\s\S]*?>[\s\S]*?<\/script>/gi,
        /javascript:/gi,
        /on\w+\s*=/gi,
        /<iframe[\s\S]*?>/gi,
        /<object[\s\S]*?>/gi,
        /<embed[\s\S]*?>/gi,
        /<link[\s\S]*?>/gi,
        /<meta[\s\S]*?>/gi,
        /data:text\/html/gi,
        /vbscript:/gi,
      ];

      if (dangerousPatterns.some((pattern) => pattern.test(sanitized))) {
        result.error = 'Input contains potentially dangerous content';
        return result;
      }
      break;
  }

  result.isValid = true;
  return result;
};

/**
 * Generates a secure random string
 * @param {number} length - Length of the string to generate
 * @returns {string} Secure random string
 */
export const generateSecureToken = (
  length = SECURITY_CONSTANTS.CSRF_TOKEN_LENGTH
) => {
  const chars =
    'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  let result = '';

  for (let i = 0; i < length; i++) {
    result += chars.charAt(
      Math.floor(crypto.getRandomValues(new Uint32Array(1))[0] % chars.length)
    );
  }

  return result;
};

/**
 * Checks if a password is commonly used or weak
 * @param {string} password - Password to check
 * @returns {boolean} True if password is commonly used
 */
export const isCommonPassword = (password) => {
  const commonPasswords = [
    'password',
    '123456',
    'password123',
    'admin',
    'qwerty',
    'letmein',
    'welcome',
    'monkey',
    '1234567890',
    'password1',
    'abc123',
    '111111',
    '123123',
    'dragon',
    'master',
  ];

  const normalizedPassword = password.toLowerCase();
  return (
    commonPasswords.includes(normalizedPassword) ||
    commonPasswords.some((common) => normalizedPassword.includes(common))
  );
};

/**
 * Validates and sanitizes URL parameters
 * @param {string} param - Parameter to validate
 * @param {Set} allowedValues - Set of allowed values
 * @returns {Object} Validation result
 */
export const validateUrlParameter = (param, allowedValues) => {
  const result = {
    isValid: false,
    sanitized: null,
    error: null,
  };

  if (!param || typeof param !== 'string') {
    result.error = 'Parameter is required and must be a string';
    return result;
  }

  const sanitized = param.toLowerCase().trim();
  result.sanitized = sanitized;

  if (!allowedValues.has(sanitized)) {
    result.error = 'Invalid parameter value';
    return result;
  }

  result.isValid = true;
  return result;
};

export default {
  PASSWORD_REQUIREMENTS,
  VALIDATION_PATTERNS,
  SECURITY_CONSTANTS,
  validatePasswordStrength,
  validateJWTToken,
  sanitizeInput,
  generateSecureToken,
  isCommonPassword,
  validateUrlParameter,
};
